import React, { useContext, useEffect, useState } from "react";
import { usePopper } from "react-popper";
import styled, { css } from "styled-components";

import { Highlighter } from "../../../utils/highlighter";
import { HighlighterContext } from "../../../utils/useHighlighter";

const StyledHighlighterToolbar = styled.div<{ hide?: boolean }>`
  display: flex;
  gap: 8px;

  background-color: white;
  border: solid 1px #ddd;
  border-radius: 4px;
  padding: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);

  ${({ hide }) =>
    hide
      ? css`
          visibility: hidden;
          pointer-events: none;
        `
      : null}

  z-index: 1;
`;

const StyledHighligherButton = styled.button<{ color: string }>`
  background-color: ${({ color }) => color};
  border: 1px solid hsl(from ${({ color }) => color} H S calc(L - 20));

  border-radius: 10px;
  width: 20px;
  height: 20px;
`;

const StyledRemoveHighlightButton = styled.button`
  background-color: transparent;
  border: 1px solid #888;

  border-radius: 10px;
  width: 20px;
  height: 20px;

  // cross
  position: relative;
  &::before {
    content: "";
    position: absolute;
    top: 0px;
    left: 0px;
    width: 107%;
    height: 1px;
    background: rgb(136, 136, 136);
    transform-origin: center center 0px;
    transform: rotate(45deg) translate(6px, 7px);
  }
`;
type HighlighterProps = {
  highlighterRef: React.RefObject<Highlighter>;
};
const HighlighterToolbar = (props: HighlighterProps) => {
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const highlighterRef = props.highlighterRef;

  const { enabled } = useContext(HighlighterContext) ?? { enabled: false };

  const [currentSelection, setCurrentSelection] = useState<Range | null>(null);
  useEffect(() => {
    const onSelectionChange = () => {
      const selection = document.getSelection();
      if (selection && selection.rangeCount >= 1) {
        setCurrentSelection(selection.getRangeAt(0));
      } else {
        setCurrentSelection(null);
      }
    };

    if (enabled) {
      document.addEventListener("selectionchange", onSelectionChange);
    }

    return () => {
      document.removeEventListener("selectionchange", onSelectionChange);
    };
  }, [enabled]);

  // https://popper.js.org/react-popper/v2/virtual-elements/
  const { styles, attributes } = usePopper(currentSelection, ref, {
    placement: "top",
    modifiers: [
      {
        name: "offset",
        enabled: true,
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  return (
    <StyledHighlighterToolbar
      ref={setRef}
      style={styles.popper}
      {...attributes.popper}
      hide={!highlighterRef.current?.shouldHighlight()}
    >
      <StyledHighligherButton
        color={Highlighter.colors.yellow}
        title="Yellow"
        onClick={() => highlighterRef.current?.createHighlight("yellow")}
      />
      <StyledHighligherButton
        color={Highlighter.colors.red}
        title="Red"
        onClick={() => highlighterRef.current?.createHighlight("red")}
      />
      <StyledHighligherButton
        color={Highlighter.colors.blue}
        title="Blue"
        onClick={() => highlighterRef.current?.createHighlight("blue")}
      />
      <StyledHighligherButton
        color={Highlighter.colors.purple}
        title="Purple"
        onClick={() => highlighterRef.current?.createHighlight("purple")}
      />
      {Highlighter.isExistingHighlight(
        currentSelection?.startContainer ?? null,
      ) ? (
        <StyledRemoveHighlightButton
          title="Remove Highlight"
          onClick={() => {
            highlighterRef.current?.removeHighlight();
          }}
        />
      ) : null}
    </StyledHighlighterToolbar>
  );
};

export default HighlighterToolbar;
